import { CheckboxListFormik } from '@strata/react-checkbox-list';
import { DialogWithForm } from '@strata/react-dialog';
import { TextInputFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import {
    compactCriteria,
    CRITERIA_FIELDS,
    DEFAULT_QUEUE_SORT,
    SORT_OPTIONS,
} from 'services/requestQueueHelpers';
import {
    useCreateRequestQueueMutation,
    useUpdateRequestQueueMutation,
} from 'store/api/agoraMonitorApi';

const VISIBILITY_OPTIONS = [
    { value: 'personal', text: 'Personal' },
    { value: 'shared', text: 'Everyone' },
];
const SORT_FIELD_OPTIONS = SORT_OPTIONS.map(({ id, label }) => ({ value: id, text: label }));
const SORT_DIRECTION_OPTIONS = [
    { value: 'asc', text: 'Ascending' },
    { value: 'desc', text: 'Descending' },
];

const emptyCriteria = () => Object.fromEntries(CRITERIA_FIELDS.map(({ key }) => [key, []]));

const buildInitialValues = (queue) => {
    const sortEntry = queue?.sort?.[0] ?? DEFAULT_QUEUE_SORT[0];
    return {
        // Formik text inputs need a string, not undefined.
        name: queue?.name ?? '',
        visibility: queue?.visibility === 'shared' ? 'shared' : 'personal',
        ...emptyCriteria(),
        ...queue?.criteria,
        sortId: sortEntry.id ?? DEFAULT_QUEUE_SORT[0].id,
        sortDirection: sortEntry.desc ? 'desc' : 'asc',
    };
};

const validationSchema = Yup.object({
    name: Yup.string()
        .trim()
        .required(validationStrings.requiredField.errorMessage)
        .max(200, 'Must be 200 characters or fewer'),
    visibility: Yup.string().required(validationStrings.requiredField.errorMessage),
    sortId: Yup.string().required(validationStrings.requiredField.errorMessage),
    sortDirection: Yup.string().required(validationStrings.requiredField.errorMessage),
});

export const RequestQueueDialog = ({
    isOpen,
    onClose,
    queue = undefined,
    criteriaOptions = undefined,
}) => {
    const isEdit = Boolean(queue);
    const [createRequestQueue] = useCreateRequestQueueMutation();
    const [updateRequestQueue] = useUpdateRequestQueueMutation();

    return (
        <Formik
            initialValues={buildInitialValues(queue)}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                const criteria = compactCriteria(
                    Object.fromEntries(CRITERIA_FIELDS.map(({ key }) => [key, values[key] ?? []])),
                );
                const sort = [
                    {
                        id: values.sortId,
                        desc: values.sortDirection === 'desc',
                    },
                ];
                if (isEdit) {
                    await updateRequestQueue({
                        queueId: queue.queueId,
                        name: values.name.trim(),
                        criteria,
                        sort,
                    }).unwrap();
                    SuccessToast({ message: `Queue "${values.name.trim()}" updated.` });
                } else {
                    await createRequestQueue({
                        name: values.name.trim(),
                        visibility: values.visibility,
                        criteria,
                        sort,
                    }).unwrap();
                    SuccessToast({ message: `Queue "${values.name.trim()}" created.` });
                }
                onClose();
            }}
        >
            {(formik) => (
                <DialogWithForm
                    isOpen={isOpen}
                    dialogTitle={isEdit ? 'Edit queue' : 'New queue'}
                    width={520}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={onClose}
                    primaryButtonLabel={isEdit ? 'Save' : 'Create'}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <TextInputFormik id="name" label="Name" required />
                            {!isEdit && (
                                <RadioButtonGroupFormik
                                    name="visibility"
                                    label="Visibility"
                                    options={VISIBILITY_OPTIONS}
                                    required
                                />
                            )}
                            {CRITERIA_FIELDS.map(({ key, label }) => (
                                <CheckboxListFormik
                                    key={key}
                                    name={key}
                                    label={label}
                                    options={criteriaOptions?.[key] ?? []}
                                />
                            ))}
                            <RadioButtonGroupFormik
                                name="sortId"
                                label="Sort by"
                                options={SORT_FIELD_OPTIONS}
                                required
                            />
                            <RadioButtonGroupFormik
                                name="sortDirection"
                                label="Sort direction"
                                options={SORT_DIRECTION_OPTIONS}
                                required
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

RequestQueueDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    queue: PropTypes.shape({
        queueId: PropTypes.string.isRequired,
        name: PropTypes.string,
        visibility: PropTypes.string,
        criteria: PropTypes.shape({
            state: PropTypes.arrayOf(PropTypes.string),
            request_type_code: PropTypes.arrayOf(PropTypes.string),
            request_category_key: PropTypes.arrayOf(PropTypes.string),
            urgency_band: PropTypes.arrayOf(PropTypes.string),
            pharmacy_npi: PropTypes.arrayOf(PropTypes.string),
            assigned_to: PropTypes.arrayOf(PropTypes.string),
        }),
        sort: PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool,
            }),
        ),
    }),
    criteriaOptions: PropTypes.shape({
        state: PropTypes.arrayOf(PropTypes.string),
        request_type_code: PropTypes.arrayOf(PropTypes.string),
        request_category_key: PropTypes.arrayOf(PropTypes.string),
        urgency_band: PropTypes.arrayOf(PropTypes.string),
        pharmacy_npi: PropTypes.arrayOf(PropTypes.string),
        assigned_to: PropTypes.arrayOf(
            PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.shape({ value: PropTypes.string, text: PropTypes.string }),
            ]),
        ),
    }),
};
